import { useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { defaultTheme } from "../config/theme";
import useShopTheme from "./useShopTheme";
import LocalStorageService from "../common/services/LocalStorageService";

// The merged theme of the last successful settings fetch, keyed by host so a
// cache left by another shop on the same browser is ignored. The inline
// script in public/index.html reads the same key before React loads.
export const THEME_CACHE_KEY = "shop_theme_cache";

const readCache = () => {
  try {
    const cached = LocalStorageService.get(THEME_CACHE_KEY);
    return cached?.host === window.location.hostname ? cached.theme : null;
  } catch (e) {
    return null;
  }
};

const writeCache = (theme) => {
  try {
    const next = { host: window.location.hostname, theme };
    if (
      JSON.stringify(LocalStorageService.get(THEME_CACHE_KEY)) !==
      JSON.stringify(next)
    ) {
      LocalStorageService.set(THEME_CACHE_KEY, next);
    }
  } catch (e) {}
};

export default function useThemeManager() {
  const shop = useSelector((state) => state?.shopDetails?.shop);
  const loading = useSelector((state) => state?.shopDetails?.loading);
  const fetched = useShopTheme();
  const cached = useMemo(readCache, []);

  // Until the fetch resolves, render the cached theme (or the default when
  // nothing is cached). After it resolves, the fetched merge wins.
  const currentTheme = shop ? fetched : cached || defaultTheme;
  const themeReady = Boolean(shop) || Boolean(cached);

  const setCssVariables = useCallback((theme) => {
    const root = document?.documentElement?.style;
    if (!root) return;
    const set = (name, value) => {
      if (value) root.setProperty(name, value);
    };
    set(
      "--swiper-slider-icon-color",
      theme?.components?.icons?.colorSwiperIcon
    );
    set("--main-bg-color", theme?.components?.Layout?.bodyBg);
    set("--primary-color", theme?.token?.colorPrimary);
    set("--text-color", theme?.token?.colorText);
    set("--text-color-tertiary", theme?.token?.colorTextTertiary);
    set("--text-main", theme?.components?.Typography?.mainColorText);
    set("--card-bg", theme?.components?.Card?.colorBgCard);
    set("--product-image-bg", theme?.token?.product_default_image_bg);
    set("--status-warning", theme?.components?.Status?.warningColor);
    set("--status-success", theme?.components?.Status?.successColor);
    set("--status-error", theme?.components?.Status?.errorColor);
    set("--color-border", theme?.token?.colorBorder);
    set("--color-error", theme?.token?.colorError);
    set("--color-warning", theme?.token?.colorWarning);
    set("--star-color", theme?.components?.Rate?.starColor);
    set("--star-bg", theme?.components?.Rate?.starBg);
  }, []);

  useEffect(() => {
    if (currentTheme) {
      setCssVariables(currentTheme);
    }
  }, [currentTheme, setCssVariables]);

  useEffect(() => {
    if (shop) writeCache(fetched);
  }, [shop, fetched]);

  return { currentTheme, themeReady, loading, setCssVariables };
}
